import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  url: string | null;
  label: string;
  testId: string;
  onAttach?: (file: File) => void;
}

const ZOOMS = [1, 1.5, 2, 3];

/** One sheet image, zoomable, client-side object URL only (EPIC-112 D5). */
export default function SheetImageViewer({
  url,
  label,
  testId,
  onAttach,
}: Props) {
  const { t } = useTranslation("admin");
  const [zoomIdx, setZoomIdx] = useState(0);
  const zoom = ZOOMS[zoomIdx];

  return (
    <div className="rounded border border-gray-200" data-testid={testId}>
      <div className="flex items-center justify-between border-b bg-gray-50 px-2 py-1 text-sm">
        <span className="font-medium">{label}</span>
        {url && (
          <span className="flex items-center gap-1">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setZoomIdx((i) => Math.max(0, i - 1))}
              disabled={zoomIdx === 0}
              aria-label={t("matchSheetImport.zoomOut")}
              data-testid={`${testId}-zoom-out`}
            >
              -
            </button>
            <span
              className="w-12 text-center text-xs"
              data-testid={`${testId}-zoom`}
            >
              {Math.round(zoom * 100)}%
            </span>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() =>
                setZoomIdx((i) => Math.min(ZOOMS.length - 1, i + 1))
              }
              disabled={zoomIdx === ZOOMS.length - 1}
              aria-label={t("matchSheetImport.zoomIn")}
              data-testid={`${testId}-zoom-in`}
            >
              +
            </button>
          </span>
        )}
      </div>
      <div className="max-h-[70vh] overflow-auto bg-gray-100">
        {url ? (
          <img
            src={url}
            alt={label}
            style={{ width: `${zoom * 100}%`, maxWidth: "none" }}
            data-testid={`${testId}-img`}
          />
        ) : (
          <div
            className="p-4 text-sm text-gray-600"
            data-testid={`${testId}-reattach`}
          >
            <p className="mb-2">{t("matchSheetImport.reattachHint")}</p>
            {onAttach && (
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label={t("matchSheetImport.reattach")}
                data-testid={`${testId}-reattach-input`}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onAttach(f);
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
